import axios from 'axios';

export const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api/v1',
    withCredentials: true,
});

// Lazy store getter — breaks the circular dependency:
// store → authSlice → apiClient → store
const getStore = (): import('../store/store').RootState extends infer S ? any : any =>
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require('../store/store').store;

const getDispatch = () => getStore().dispatch;
const getState = () => getStore().getState();

// Attach access token to every request
apiClient.interceptors.request.use((config) => {
    const token = (getState() as any).auth.accessToken as string | null;
    if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
});

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

const processQueue = (newToken: string) => {
    refreshQueue.forEach((cb) => cb(newToken));
    refreshQueue = [];
};

// On 401 — silently refresh token and retry
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry) {
            original._retry = true;

            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { setTokens, clearAuth } = require('../store/authSlice');
            const authState = (getState() as any).auth;
            const refreshToken: string | null = authState.refreshToken;

            if (!refreshToken) {
                getDispatch()(clearAuth());
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshQueue.push((token) => {
                        original.headers.Authorization = `Bearer ${token}`;
                        resolve(apiClient(original));
                    });
                });
            }

            isRefreshing = true;
            try {
                const { data } = await axios.post(
                    `${process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api/v1'}/auth/refresh`,
                    { refreshToken }
                );
                const { accessToken } = data as { accessToken: string };
                getDispatch()(setTokens({ accessToken, refreshToken }));
                processQueue(accessToken);
                original.headers.Authorization = `Bearer ${accessToken}`;
                return apiClient(original);
            } catch {
                getDispatch()(clearAuth());
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);
